import React, { useRef, useEffect } from 'react';
import {
  Box,
  VStack,
  Text,
  Flex,
  Spinner,
  Alert,
  AlertIcon,
  Link,
  Icon,
  Badge,
  useColorModeValue,
  Divider
} from '@chakra-ui/react';
import moment from 'moment';
import { FiDownload } from 'react-icons/fi';
import { formatWhatsAppMessage } from './formatters';

/**
 * Component to display WhatsApp chat messages
 */
const ChatMessages = ({ 
  chatHistory, 
  isLoading, 
  isLoadingMore,
  error, 
  messagesEndRef,
  chatContainerRef,
  handleScroll
}) => {
  const outgoingMsgBg = useColorModeValue('#123d2a', '#2f3436');
  const outgoingMsgText = useColorModeValue('white', 'white');
  const incomingMsgBg = useColorModeValue('gray.100', 'gray.600');
  const incomingMsgText = useColorModeValue('black', 'white');
  const authorNameColor = useColorModeValue('blue.500', 'blue.300');
  const dateSeparatorBg = useColorModeValue('gray.100', 'gray.700');
  const dateSeparatorText = useColorModeValue('gray.600', 'gray.300');
  
  // Refs to store scroll positions and track loading state
  const prevHeightRef = useRef(0);
  const prevScrollTopRef = useRef(0);
  const isFirstLoadRef = useRef(true);
  
  // Save scroll position just before loading more messages
  useEffect(() => {
    if (isLoadingMore && chatContainerRef.current) {
      prevHeightRef.current = chatContainerRef.current.scrollHeight;
      prevScrollTopRef.current = chatContainerRef.current.scrollTop;
      console.log('[ChatMessages] Stored scroll position before loading:', {
        height: prevHeightRef.current,
        scrollTop: prevScrollTopRef.current
      });
    }
  }, [isLoadingMore]);
  
  // Restore scroll position after loading more messages
  useEffect(() => {
    if (!isLoading && !isLoadingMore && chatContainerRef.current) {
      if (isFirstLoadRef.current) {
        // For the first load, scroll to bottom
        isFirstLoadRef.current = false;
        messagesEndRef.current?.scrollIntoView({ behavior: 'instant' });
        console.log('[ChatMessages] Initial load, scrolled to bottom');
      } else if (prevHeightRef.current > 0) {
        // After loading more messages, maintain scroll position
        const newHeight = chatContainerRef.current.scrollHeight;
        const heightDiff = newHeight - prevHeightRef.current;
        
        if (heightDiff > 0) {
          // Set scroll position to maintain the same relative position
          // We need to add the height difference to maintain position
          const newScrollTop = prevScrollTopRef.current + heightDiff;
          chatContainerRef.current.scrollTop = newScrollTop;
          
          console.log('[ChatMessages] Adjusted scroll position after loading:', {
            oldHeight: prevHeightRef.current,
            newHeight: newHeight,
            heightDiff: heightDiff,
            newScrollTop: newScrollTop
          });
        }
        
        // Reset stored values
        prevHeightRef.current = 0;
        prevScrollTopRef.current = 0;
      }
    }
  }, [chatHistory, isLoading, isLoadingMore]);
  
  // Group messages by date
  const groupMessagesByDate = (messages) => {
    if (!Array.isArray(messages) || messages.length === 0) return [];
    
    const groups = {};
    
    messages.forEach(msg => {
      if (!msg) return;
      
      // Handle different timestamp formats
      let timestamp;
      if (msg.timestamp) {
        timestamp = typeof msg.timestamp === 'number' 
          ? moment.unix(msg.timestamp) 
          : moment(msg.timestamp);
      } else {
        timestamp = moment();
      }
      
      if (timestamp.isValid()) {
        const dateKey = timestamp.format('YYYY-MM-DD');
        if (!groups[dateKey]) {
          groups[dateKey] = {
            date: timestamp.format('dddd, MMMM D, YYYY'),
            messages: []
          };
        }
        groups[dateKey].messages.push(msg);
      } else {
        // Handle invalid timestamps
        if (!groups['unknown']) {
          groups['unknown'] = {
            date: 'Unknown Date',
            messages: []
          };
        }
        groups['unknown'].messages.push(msg);
      }
    });
    
    // Convert to array and sort by date
    return Object.values(groups).sort((a, b) => {
      if (a.date === 'Unknown Date') return 1;
      if (b.date === 'Unknown Date') return -1;
      return moment(a.date, 'dddd, MMMM D, YYYY').diff(moment(b.date, 'dddd, MMMM D, YYYY'));
    });
  };
  
  // Message status indicators like in the original component
  const getMessageStatus = (ackName) => {
    switch (ackName) {
      case 'READ': return '✓✓ Read';
      case 'PLAYED': return '✓✓ Played';
      case 'DEVICE': return '✓✓ Delivered';
      case 'SERVER': return '✓ Sent';
      case 'PENDING': return '🕒 Pending';
      default: return '';
    }
  };

  // Message bubble component for better message rendering
  const MessageBubble = ({ message }) => {
    // Define all color mode values at the top level of the component
    const whiteAlphaText = useColorModeValue('whiteAlpha.700', 'whiteAlpha.500');
    const mutedTextColor = useColorModeValue('gray.600', 'gray.400');
    const linkColor = useColorModeValue('blue.500', 'blue.300');
    const linkHoverColor = useColorModeValue('blue.600', 'blue.400');
    const systemMsgBg = useColorModeValue('gray.50', 'gray.600');
    const systemMsgText = useColorModeValue('gray.600', 'gray.200');
    const systemMsgTimeText = useColorModeValue('gray.500', 'gray.400');
    
    // Determine message type and properties
    const isFromMe = message.fromMe || message.direction === 'outgoing';
    const hasFile = message.hasMedia && message.mediaUrl;
    const isGroupSystemMessage = message.type === 'gp2';
    const messageBody = message.body || message.content || '';
    
    // For timestamp handling
    let timestamp;
    if (message.timestamp) {
      // Handle different timestamp formats (unix timestamp or ISO string)
      timestamp = typeof message.timestamp === 'number' 
        ? moment.unix(message.timestamp) 
        : moment(message.timestamp);
    } else {
      timestamp = moment();
    }
    
    // Handle group system messages
    if (isGroupSystemMessage) {
      return (
        <Flex justify="center" w="100%" mb={2}>
          <Box
            bg={systemMsgBg}
            px={3}
            py={2}
            borderRadius="lg"
            maxW="80%"
            textAlign="center"
          >
            <Text fontSize="sm" color={systemMsgText}>
              {message.subtype === 'create' ? (
                <Text as="span">
                  <Text as="span" fontWeight="bold">Group Created:</Text>
                  {' '}{messageBody}
                </Text>
              ) : messageBody}
            </Text>
            <Text fontSize="xs" color={systemMsgTimeText} mt={1}>
              {timestamp.isValid() ? timestamp.format('MMM D, h:mm A') : '—'}
            </Text>
          </Box>
        </Flex>
      );
    }
    
    // Handle regular messages
    return (
      <Flex justify={isFromMe ? 'flex-end' : 'flex-start'} w="100%" mb={2}>
        <Box
          bg={isFromMe ? outgoingMsgBg : incomingMsgBg}
          color={isFromMe ? outgoingMsgText : incomingMsgText}
          px={3}
          py={2}
          borderRadius="lg"
          maxW="80%"
          whiteSpace="pre-wrap"
        >
          {message.author && !isFromMe && (
            <Text fontSize="xs" fontWeight="bold" mb={1} color={authorNameColor}>
              {typeof message.author === 'string' ? message.author : 
               message.author.name || (message.author.id && message.author.id.split('@')[0])}
            </Text>
          )}
          
          {hasFile && (
            <Link 
              href={message.mediaUrl}
              isExternal
              display="block"
              mb={2}
              color={isFromMe ? outgoingMsgText : linkColor}
              textDecoration="underline"
              _hover={{ color: isFromMe ? whiteAlphaText : linkHoverColor }}
            >
              <Flex align="center">
                <Icon as={FiDownload} mr={2} />
                {message.fileName || 'Download File'}
              </Flex>
            </Link>
          )}
          
          <Box fontSize="sm">
            {formatWhatsAppMessage(messageBody)}
          </Box>
          
          <Flex justify="space-between" align="center" mt={1}>
            <Text fontSize="xs" color={isFromMe ? whiteAlphaText : mutedTextColor}>
              {timestamp.isValid() ? timestamp.format('h:mm A') : '—'}
            </Text>
            {isFromMe && message.ackName && (
              <Text fontSize="xs" color={whiteAlphaText} ml={2}>
                {getMessageStatus(message.ackName)}
              </Text>
            )}
          </Flex>
        </Box>
      </Flex>
    );
  };

  // Create date-grouped messages
  const messageGroups = groupMessagesByDate(chatHistory);

  return (
    <Box 
      flex={1} 
      p={3} 
      overflowY="auto" 
      ref={chatContainerRef}
      onScroll={handleScroll}
      className="chat-messages-container"
    >
      {/* Loading more indicator at the top */}
      {isLoadingMore && (
        <Flex justify="center" align="center" p={2} mb={2}>
          <Spinner size="sm" color="green.500" />
          <Text ml={2} fontSize="xs">Loading earlier messages...</Text>
        </Flex>
      )}
      
      {isLoading ? (
        <Flex justify="center" align="center" h="100%">
          <Spinner size="md" color="green.500" />
          <Text ml={3} fontSize="sm">Loading messages...</Text>
        </Flex>
      ) : error ? (
        <Alert status="info" borderRadius="md" fontSize="sm">
          <AlertIcon />
          {error}
        </Alert>
      ) : (
        <VStack spacing={2} align="stretch">
          {Array.isArray(chatHistory) && chatHistory.length > 0 ? (
            // Show message count badge
            <Flex justify="center" mb={2}>
              <Badge bg="gray.200" color="gray.600" borderRadius="full" px={2}>
                {chatHistory.length} messages
              </Badge>
            </Flex>
          ) : null}
          
          {messageGroups.map((group, groupIndex) => (
            <Box key={`date-group-${groupIndex}`} mb={4}>
              {/* Date separator */}
              <Flex justify="center" mb={3} mt={groupIndex > 0 ? 3 : 0}>
                <Badge 
                  bg={dateSeparatorBg} 
                  color={dateSeparatorText} 
                  borderRadius="full" 
                  px={3}
                  py={1}
                  fontSize="xs"
                  fontWeight="medium"
                >
                  {group.date}
                </Badge>
              </Flex>
              
              {/* Messages for this date */}
              {group.messages.map((msg, idx) => {
                // Skip rendering if message is missing critical data
                if (!msg) return null;
                return (
                  <Box key={`msg-${groupIndex}-${idx}`} className="message-bubble">
                    <MessageBubble message={msg} />
                  </Box>
                );
              })}
            </Box>
          ))}
          
          <div ref={messagesEndRef} />
        </VStack>
      )}
    </Box>
  );
};

export default ChatMessages;
